import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CNAES_MEI, getCnaeBySlug, tipoLabel, type CnaeMei } from "@/lib/cnaes-mei";
import { IaCnaeMei } from "@/components/tools/IaCnaeMei";
import { CheckCircle2, AlertCircle, Receipt, TrendingUp, ArrowRight, Bot } from "lucide-react";
import { RelatedTools } from "@/components/RelatedTools";
import { AuthorBlock } from "@/components/AuthorBlock";

const SITE = "https://meitoolbox.com.br";

const FAQS_GERAIS = [
  {
    q: "Qual CNAE devo escolher para MEI?",
    a: "O CNAE ideal é o que mais se aproxima da sua atividade principal. Use a ferramenta IA CNAE MEI do MEI Toolbox para buscar por nome da profissão (ex.: designer gráfico, cabeleireiro, eletricista) e descobrir o código exato, o valor do DAS e se a atividade é permitida.",
  },
  {
    q: "Como descobrir meu CNAE?",
    a: "Você pode pesquisar pelo nome da sua atividade na ferramenta IA CNAE MEI ou consultar a tabela oficial de CNAEs no site do IBGE. Cada ocupação tem um código numérico de 7 dígitos que deve constar no seu cartão CNPJ.",
  },
  {
    q: "Quais atividades são permitidas no MEI?",
    a: "O MEI permite mais de 500 atividades, divididas em comércio, serviço e indústria. Profissões como cabeleireiro, designer, programador, eletricista e entregador são permitidas. Atividades como advocacia, medicina e engenharia não podem ser exercidas como MEI.",
  },
  {
    q: "Quanto custa o DAS para este CNAE?",
    a: "O valor do DAS depende do tipo de atividade: serviços custam R$ 75,60/mês, comércio R$ 71,60/mês e atividades mistas (comércio + serviço) também R$ 71,60/mês. O pagamento inclui INSS, ISS e/ou ICMS em uma única guia.",
  },
  {
    q: "Posso ter mais de um CNAE no MEI?",
    a: "Sim. O MEI pode ter até 15 CNAEs vinculados ao CNPJ, desde que todas as atividades estejam na lista permitida para o Microempreendedor Individual e façam parte do mesmo ramo de negócio.",
  },
];

export const Route = createFileRoute("/ferramentas/ia-cnae-mei/$slug")({
  loader: ({ params }) => {
    const cnae = getCnaeBySlug(params.slug);
    if (!cnae) throw notFound();
    return cnae;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return {};
    const c = loaderData;
    const url = `${SITE}/ferramentas/ia-cnae-mei/${params.slug}`;
    const title = `${c.nome} pode ser MEI? CNAE ${c.codigo} e DAS 2026`;
    const description = `${c.nome} ${c.permitido ? "é permitido" : "não é permitido"} no MEI. CNAE ${c.codigo}, DAS de R$ ${c.das.toFixed(2).replace(".", ",")} por mês, vantagens e regras atualizadas.`;
    return {
      meta: [
        { title: title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: c.faq.concat(FAQS_GERAIS).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Início", item: `${SITE}/` },
              {
                "@type": "ListItem",
                position: 2,
                name: "Ferramentas",
                item: `${SITE}/ferramentas`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "IA CNAE MEI",
                item: `${SITE}/ferramentas/ia-cnae-mei`,
              },
              { "@type": "ListItem", position: 4, name: c.nome, item: url },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: title,
            description,
            datePublished: "2026-01-01",
            dateModified: "2026-06-01",
            author: {
              "@type": "Person",
              name: "Ismaque de Oliveira",
              jobTitle: "Fundador do MEI Toolbox",
            },
            publisher: { "@type": "Organization", name: "MEI Toolbox" },
            mainEntityOfPage: url,
          }),
        },
      ],
    };
  },
  component: CnaeSeoPage,
});

function CnaeSeoPage() {
  const c = Route.useLoaderData() as CnaeMei;
  const inss = c.das - (c.tipo === "servico" ? 5 : c.tipo === "comercio" ? 1 : 6);

  return (
    <section className="container mx-auto max-w-4xl px-4 py-8">
      <nav className="text-xs text-muted-foreground mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-foreground">
          Início
        </Link>
        <span className="mx-1.5">/</span>
        <Link to="/ferramentas" className="hover:text-foreground">
          Ferramentas
        </Link>
        <span className="mx-1.5">/</span>
        <span className="text-foreground">IA CNAE MEI · {c.nome}</span>
      </nav>

      <header className="rounded-3xl border border-border bg-gradient-card p-6 md:p-8 shadow-elevated">
        <div className="flex items-center gap-2 text-primary">
          <Bot className="h-5 w-5" />
          <span className="text-xs font-bold uppercase tracking-wide">Análise IA · CNAE MEI</span>
        </div>
        <h1 className="mt-2 text-2xl md:text-3xl font-bold">{c.nome} pode ser MEI em 2026?</h1>
        <p className="mt-2 text-muted-foreground">{c.desc}</p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${
              c.permitido
                ? "bg-success/15 text-success-foreground border border-success/30"
                : "bg-destructive/10 text-destructive border border-destructive/30"
            }`}
          >
            {c.permitido ? (
              <CheckCircle2 className="h-3.5 w-3.5" />
            ) : (
              <AlertCircle className="h-3.5 w-3.5" />
            )}
            {c.permitido ? "Permitido MEI" : "Vedado MEI"}
          </span>
          <span className="rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            CNAE {c.codigo}
          </span>
          <span className="rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            {tipoLabel(c.tipo)}
          </span>
        </div>
      </header>

      <div className="mt-6">
        <AuthorBlock />
      </div>

      <article className="mt-8 space-y-8">
        <section>
          <h2 className="text-xl font-semibold mb-3">
            CNAE recomendado para {c.nome.toLowerCase()}
          </h2>
          <p className="text-muted-foreground">
            O CNAE (Classificação Nacional de Atividades Econômicas) recomendado para quem atua como{" "}
            <strong>{c.nome.toLowerCase()}</strong> é o <strong>{c.codigo}</strong>, classificado
            como <strong>{tipoLabel(c.tipo).toLowerCase()}</strong>. Esse código é o que aparece no
            seu cartão CNPJ e determina os impostos cobrados na guia DAS-MEI.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Depois de escolher o CNAE, use a{" "}
            <Link
              to="/ferramentas/$slug"
              params={{ slug: "calculadora-das-mei" }}
              className="text-primary hover:underline font-medium"
            >
              Calculadora DAS MEI
            </Link>{" "}
            para saber o valor exato da guia mensal.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">Descrição da atividade</h2>
          <p className="text-muted-foreground">{c.desc}</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Precisa de um contrato de prestação de serviços? Use o{" "}
            <Link
              to="/ferramentas/$slug"
              params={{ slug: "gerador-contrato" }}
              className="text-primary hover:underline font-medium"
            >
              Gerador de Contrato Simples
            </Link>{" "}
            para criar um documento editável em minutos.
          </p>
        </section>

        <section className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
            <Receipt className="h-4 w-4" /> Valor do DAS MEI em 2026
          </div>
          <h2 className="mt-2 text-lg font-semibold">
            Quanto custa o DAS para {c.nome.toLowerCase()}?
          </h2>
          <div className="mt-3 grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-[11px] text-muted-foreground">INSS</div>
              <div className="text-base font-bold">R$ {inss.toFixed(2).replace(".", ",")}</div>
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground">
                {c.tipo === "servico" ? "ISS" : "ICMS"}
              </div>
              <div className="text-base font-bold">
                {c.tipo === "servico" ? "R$ 5,00" : c.tipo === "comercio" ? "R$ 1,00" : "R$ 6,00"}
              </div>
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground">Total DAS</div>
              <div className="text-base font-bold text-primary">
                R$ {c.das.toFixed(2).replace(".", ",")}
              </div>
            </div>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            O valor é pago mensalmente até o dia 20 e garante INSS, aposentadoria por idade e demais
            benefícios previdenciários.
          </p>
        </section>

        <section className="grid gap-3 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
              <TrendingUp className="h-4 w-4 text-success" /> Vantagens
            </div>
            <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
              {c.vantagens.map((v) => (
                <li key={v} className="flex gap-2">
                  <span className="text-success">•</span>
                  <span>{v}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              Acompanhe seu faturamento com o{" "}
              <Link
                to="/ferramentas/$slug"
                params={{ slug: "limite-faturamento-mei" }}
                className="text-primary hover:underline font-medium"
              >
                Calculadora de Limite MEI
              </Link>{" "}
              para não correr risco de desenquadramento.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
              <AlertCircle className="h-4 w-4 text-warning" /> Pontos de atenção
            </div>
            <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
              {c.riscos.map((r) => (
                <li key={r} className="flex gap-2">
                  <span className="text-warning">•</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              Se o faturamento crescer, simule a mudança para ME com o{" "}
              <Link
                to="/ferramentas/$slug"
                params={{ slug: "simulador-desenquadramento" }}
                className="text-primary hover:underline font-medium"
              >
                Simulador de Desenquadramento
              </Link>
              .
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">Impostos e obrigações</h2>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            {c.impostos.map((i) => (
              <li key={i} className="flex gap-2">
                <span className="text-primary">•</span>
                <span>{i}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-sm text-muted-foreground">
            Quer saber se precisa pagar IRPF? Use o{" "}
            <Link
              to="/ferramentas/$slug"
              params={{ slug: "simulador-irpf-mei" }}
              className="text-primary hover:underline font-medium"
            >
              Simulador IRPF MEI
            </Link>{" "}
            para estimar seu imposto de renda.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">Atividades relacionadas</h2>
          <p className="text-muted-foreground mb-3">
            Outras ocupações próximas a {c.nome.toLowerCase()} permitidas no MEI:
          </p>
          <div className="flex flex-wrap gap-2">
            {c.relacionados.map((r) => (
              <span
                key={r}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground"
              >
                {r}
              </span>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4">Perguntas frequentes</h2>
          <div className="space-y-4">
            {c.faq.concat(FAQS_GERAIS).map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-4">
                <h3 className="font-semibold">{f.q}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">Outras atividades MEI populares</h2>
          <div className="flex flex-wrap gap-2">
            {CNAES_MEI.filter((x) => x.slug !== c.slug).map((x) => (
              <Link
                key={x.slug}
                to="/ferramentas/ia-cnae-mei/$slug"
                params={{ slug: x.slug }}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground hover:border-primary hover:text-foreground transition"
              >
                {x.nome}
              </Link>
            ))}
          </div>
        </section>

        <RelatedTools
          heading="Ferramentas relacionadas"
          slugs={[
            "limite-faturamento-mei",
            "simulador-desenquadramento",
            "simulador-irpf-mei",
            "calculadora-das-mei",
            "gerador-contrato",
          ]}
          max={5}
        />

        <Link
          to="/ferramentas/ia-mei"
          className="flex items-center justify-center gap-2 w-full rounded-xl border-2 border-primary/40 bg-primary/5 px-4 py-3 text-sm font-semibold text-primary hover:bg-primary/10 transition"
        >
          Tirar dúvidas sobre {c.nome} com a IA
          <ArrowRight className="h-4 w-4" />
        </Link>

        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-3">Verifique outra atividade</h2>
          <IaCnaeMei />
        </div>
      </article>
    </section>
  );
}
